import { useMemo } from 'react'
import type { RangeKey } from '@dataTypes/dashboard.types'
import type { PeriodComparisonMetric } from '@services/api/dashboard/finance.api'
import {
  getProceduresDetail,
  type ProceduresOverviewResult,
  getProceduresOverview,
} from '@services/api/dashboard/overview.api'
import {
  buildComparisonFooter,
  computeChartAxis,
  filterYtdPeriodItems,
  financeTrendLine,
  formatCount,
  formatCurrency,
  formatPercent,
  HYGIENE_PLACEHOLDER,
} from '@utils/dashboardFinance.util'
import { FINANCE_VS } from '@constants/Dashboard/financeDashboard.constants'
import type { DashboardMetricItemData } from '@utils/dashboardMetrics.util'
import { hasNonZeroDashboardValues } from '@utils/dashboardSectionEmpty.util'
import {
  useDashboardPeriodFetch,
  useDashboardPeriodParams,
  type DashboardPeriodFetcher,
} from './useDashboardPeriod'

export type UseProceduresOptions = {
  range: RangeKey
  date: string
  enabled?: boolean
  queued?: boolean
}

export type ProceduresOverviewCardTableRow = {
  id: string
  category: string
  noOfProcedures: number | string
  production: number
  avgPerProcedure: string
}

export type ProceduresDetailKpi = {
  totalProcedures: number
  totalProceduresGoal: string
  avgProductionPerProcedure: string
  sameDayProcedures: number
  totalProduction: string
  comparisonLabel: string
  totalDelta: string
  totalTrend: 'up' | 'down'
  avgProductionDelta: string
  avgProductionTrend: 'up' | 'down'
  productionDelta: string
  productionTrend: 'up' | 'down'
}

export type ProceduresChartDataPoint = Record<string, string | number>

export type ProceduresChartSeries = {
  dataKey: string
  name: string
  color: string
}

export type ProceduresByCategoryRow = {
  id: string
  category: string
  procedures: number | string
  production: string
  avgPerProcedure: string
  pctOfTotal: string
  sameDayCount: number
}

export type ProceduresByProviderRow = {
  id: string
  provider: string
  procedures: number
  production: string
  avgPerProcedure: string
  sameDayCount: number
}

export type ProceduresByTimePeriodRow = {
  id: string
  timePeriod: string
  procedures: number
  production: string
  avgPerProcedure: string
  sameDayCount: number
}

const CHART_COLORS = [
  'var(--blue-400)',
  'var(--orange-300)',
  'var(--teal-400)',
  'var(--vivid-green-400)',
  'var(--blue-600)',
  'var(--orange-500)',
]

const EMPTY_METRIC: PeriodComparisonMetric = {
  current: 0,
  previous: 0,
  percentChange: 0,
}

function metricFooter(
  range: RangeKey,
  metric: PeriodComparisonMetric,
  options?: { isCurrency?: boolean; isCount?: boolean },
): { footer: string; footerTrend: 'up' | 'down' } {
  const { up } = buildComparisonFooter(metric, options)
  const pct = Math.abs(metric.percentChange)
  const sign = metric.percentChange >= 0 ? '+' : '-'
  const delta = metric.current - metric.previous
  const amt = options?.isCount
    ? formatCount(Math.abs(delta))
    : formatCurrency(Math.abs(delta), { compact: true })
  return {
    footer: financeTrendLine(range, `${sign}${pct.toFixed(1)}%`, amt),
    footerTrend: up ? 'up' : 'down',
  }
}

function detailTrendDelta(
  metric: PeriodComparisonMetric,
  options?: { isCurrency?: boolean; isCount?: boolean },
): { delta: string; trend: 'up' | 'down' } {
  const { up } = buildComparisonFooter(metric, options)
  const pct = Math.abs(metric.percentChange)
  const sign = metric.percentChange >= 0 ? '+' : '-'
  const delta = metric.current - metric.previous
  const amt = options?.isCount
    ? formatCount(Math.abs(delta))
    : formatCurrency(Math.abs(delta), { compact: true })
  return {
    delta: `${sign}${pct.toFixed(1)}% (${amt})`,
    trend: up ? 'up' : 'down',
  }
}

function buildCardMetrics(
  range: RangeKey,
  summary: ProceduresOverviewResult['summary'] | undefined,
  loading: boolean,
): DashboardMetricItemData[] {
  if (loading || !summary) {
    return [
      {
        id: 'total-procedures',
        leftLabel: 'Total procedures completed',
        value: HYGIENE_PLACEHOLDER,
      },
      {
        id: 'avg-production',
        leftLabel: 'Avg. production per procedure',
        value: HYGIENE_PLACEHOLDER,
      },
      {
        id: 'same-day-procedure',
        leftLabel: 'Same day procedure',
        value: HYGIENE_PLACEHOLDER,
      },
    ]
  }

  const total = summary.totalProceduresCompleted
  const avg = summary.averageProductionPerProcedure
  const sameDay = summary.sameDayProcedures

  return [
    {
      id: 'total-procedures',
      leftLabel: 'Total procedures completed',
      value: formatCount(total.current),
      ...metricFooter(range, total, { isCount: true }),
    },
    {
      id: 'avg-production',
      leftLabel: 'Avg. production per procedure',
      value: formatCurrency(avg.current, { compact: true }),
      ...metricFooter(range, avg, { isCurrency: true }),
    },
    {
      id: 'same-day-procedure',
      leftLabel: 'Same day procedure',
      value: formatCount(sameDay.current),
      ...metricFooter(range, sameDay, { isCount: true }),
    },
  ]
}

export type UseProceduresResult = {
  loading: boolean
  error: string | null
  hasData: boolean
  /** True once the response has arrived (independent of meaningful content). */
  hasPayload: boolean
  range: RangeKey
  cardMetrics: DashboardMetricItemData[]
  cardTableRows: ProceduresOverviewCardTableRow[]
  kpi: ProceduresDetailKpi
  chartData: ProceduresChartDataPoint[]
  chartSeries: ProceduresChartSeries[]
  chartCategories: string[]
  chartYAxisDomain: [number, number]
  chartYAxisTicks: number[]
  byCategoryRows: ProceduresByCategoryRow[]
  byProviderRows: ProceduresByProviderRow[]
  byTimePeriodRows: ProceduresByTimePeriodRow[]
}

function useProceduresFromFetcher({
  range,
  date,
  enabled = true,
  queued = false,
  fetcher,
}: UseProceduresOptions & {
  fetcher: DashboardPeriodFetcher<
    Awaited<ReturnType<typeof getProceduresDetail>>
  >
}): UseProceduresResult {
  const queryParams = useDashboardPeriodParams(range, date)
  const { data, loading, error } = useDashboardPeriodFetch(
    queryParams,
    fetcher,
    { enabled, queued },
  )

  const summary = data?.summary

  const cardMetrics = useMemo(
    () => buildCardMetrics(range, summary, loading),
    [range, summary, loading],
  )

  const cardTableRows = useMemo((): ProceduresOverviewCardTableRow[] => {
    if (!data?.byCategory?.length) return []
    return data.byCategory.map((row, i) => ({
      id: String(i + 1),
      category: row.category,
      noOfProcedures: row.procedureCount,
      production: row.production,
      avgPerProcedure: formatCurrency(row.averageProductionPerProcedure, {
        compact: true,
      }),
    }))
  }, [data])

  const kpi = useMemo((): ProceduresDetailKpi => {
    const s = summary ?? {
      totalProceduresCompleted: EMPTY_METRIC,
      averageProductionPerProcedure: EMPTY_METRIC,
      sameDayProcedures: EMPTY_METRIC,
      totalProduction: EMPTY_METRIC,
    }
    const totalTrend = detailTrendDelta(s.totalProceduresCompleted, {
      isCount: true,
    })
    const avgTrend = detailTrendDelta(s.averageProductionPerProcedure, {
      isCurrency: true,
    })
    const prodTrend = detailTrendDelta(s.totalProduction, {
      isCurrency: true,
    })
    return {
      totalProcedures: Math.round(s.totalProceduresCompleted.current),
      totalProceduresGoal: HYGIENE_PLACEHOLDER,
      avgProductionPerProcedure: formatCurrency(
        s.averageProductionPerProcedure.current,
        { compact: true },
      ),
      sameDayProcedures: Math.round(s.sameDayProcedures.current),
      totalProduction: formatCurrency(s.totalProduction.current, {
        compact: true,
      }),
      comparisonLabel: FINANCE_VS[range],
      totalDelta: totalTrend.delta,
      totalTrend: totalTrend.trend,
      avgProductionDelta: avgTrend.delta,
      avgProductionTrend: avgTrend.trend,
      productionDelta: prodTrend.delta,
      productionTrend: prodTrend.trend,
    }
  }, [summary, range])

  const chartCategories = useMemo(
    () => data?.proceduresByCategoryPeriodSummary?.map((s) => s.category) ?? [],
    [data],
  )

  const { chartData, chartSeries, chartYAxisDomain, chartYAxisTicks } =
    useMemo(() => {
      const seriesList = data?.proceduresByCategoryPeriodSummary ?? []
      if (!seriesList.length) {
        return {
          chartData: [] as ProceduresChartDataPoint[],
          chartSeries: [] as ProceduresChartSeries[],
          chartYAxisDomain: [0, 1] as [number, number],
          chartYAxisTicks: [0, 1],
        }
      }

      const filtered = seriesList.map((s) => ({
        category: s.category,
        buckets: filterYtdPeriodItems(
          range,
          date,
          s.periodSummary,
          (b) => b.label,
        ),
      }))

      const labels = filtered[0]?.buckets.map((b) => b.label) ?? []
      const chartDataPoints: ProceduresChartDataPoint[] = labels.map(
        (label, i) => {
          const point: ProceduresChartDataPoint = { label }
          filtered.forEach((s) => {
            const key = s.category.replace(/\s+/g, '_')
            point[key] = s.buckets[i]?.value ?? 0
          })
          return point
        },
      )

      const chartSeriesItems: ProceduresChartSeries[] = filtered.map(
        (s, i) => ({
          dataKey: s.category.replace(/\s+/g, '_'),
          name: s.category,
          color: CHART_COLORS[i % CHART_COLORS.length],
        }),
      )

      const values = chartDataPoints.flatMap((p) =>
        chartSeriesItems.map((s) => Number(p[s.dataKey] ?? 0)),
      )
      const axis = computeChartAxis(values)

      return {
        chartData: chartDataPoints,
        chartSeries: chartSeriesItems,
        chartYAxisDomain: axis.yDomain,
        chartYAxisTicks: axis.yTicks,
      }
    }, [data, range, date])

  const byCategoryRows = useMemo((): ProceduresByCategoryRow[] => {
    return (data?.byCategory ?? []).map((row, i) => ({
      id: String(i + 1),
      category: row.category,
      procedures: row.procedureCount,
      production: formatCurrency(row.production, { compact: true }),
      avgPerProcedure: formatCurrency(row.averageProductionPerProcedure, {
        compact: true,
      }),
      pctOfTotal: formatPercent(row.percentOfTotalProduction, 1),
      sameDayCount: row.sameDayProductionCount,
    }))
  }, [data])

  const byProviderRows = useMemo((): ProceduresByProviderRow[] => {
    return (data?.byProvider ?? []).map((row, i) => ({
      id: String(i + 1),
      provider: row.provider,
      procedures: row.procedureCount,
      production: formatCurrency(row.production, { compact: true }),
      avgPerProcedure: formatCurrency(row.averageProductionPerProcedure, {
        compact: true,
      }),
      sameDayCount: row.sameDayProcedureCount,
    }))
  }, [data])

  const byTimePeriodRows = useMemo((): ProceduresByTimePeriodRow[] => {
    const rows = filterYtdPeriodItems(
      range,
      date,
      data?.byTimePeriod ?? [],
      (r) => r.period,
    )
    return rows.map((row, i) => ({
      id: String(i + 1),
      timePeriod: row.period,
      procedures: row.procedureCount,
      production: formatCurrency(row.production, { compact: true }),
      avgPerProcedure: formatCurrency(row.averageProductionPerProcedure, {
        compact: true,
      }),
      sameDayCount: row.sameDayProcedureCount,
    }))
  }, [data, range, date])

  return {
    loading,
    error,
    hasData:
      hasNonZeroDashboardValues(
        data?.summary.totalProceduresCompleted.current,
        data?.summary.averageProductionPerProcedure.current,
        data?.summary.sameDayProcedures.current,
        data?.summary.totalProduction.current,
      ) ||
      (data?.byCategory ?? []).some((row) =>
        hasNonZeroDashboardValues(row.procedureCount, row.production),
      ),
    hasPayload: data != null,
    range,
    cardMetrics,
    cardTableRows,
    kpi,
    chartData,
    chartSeries,
    chartCategories,
    chartYAxisDomain,
    chartYAxisTicks,
    byCategoryRows,
    byProviderRows,
    byTimePeriodRows,
  }
}

export function useProcedures(
  options: UseProceduresOptions,
): UseProceduresResult {
  return useProceduresFromFetcher({ ...options, fetcher: getProceduresDetail })
}

export function useProceduresOverview(
  options: UseProceduresOptions,
): UseProceduresResult {
  return useProceduresFromFetcher({
    ...options,
    fetcher: getProceduresOverview,
  })
}
